/*
 * PREFERENCE SELECTORS - getting data out of the preference store
 *
 * These routines require a "state" value from the preference store, to access
 * the data model from anywhere,use the preferenceStore module which will create the
 * state then call these routines
 */

import { Record } from 'immutable';
import { createSelector } from 'reselect';

import { selectorsHelpers } from 'app-components/flux/core';

import { colorLookup } from './utils';

export const REDUCER_KEY = 'preferencesStore';

export function mapJSObject(obj, fn) {
  const ret = {};
  Object.keys(obj).forEach((key) => {
    ret[key] = fn(obj[key]);
  });
  return ret;
}

/*
 * For these selectors, state is global state as it is provided to React (not specific to preferences)
 * We use them to select into the store and get data for this REDUCER_KEY
 */
export const getPreferenceStore = (store) => store[REDUCER_KEY]; // store has multiple reducer states
export const getPreferences = (state) => getPreferenceStore(state).preferences;
export const getError = (state) => getPreferenceStore(state).error;
export const getIsLoading = (state) => getPreferenceStore(state).isLoading;
export const getLoadPending = (state) =>
  getPreferenceStore(state).loadPending.shared.user || getPreferenceStore(state).loadPending.shared.dataset;
export const getLoadPendingState = (state) => getPreferenceStore(state).loadPending;

/*
 * The selectors below operate directly on the preferences datastore (getPreferenceStore)
 */

const pathIn = (obj, path) => path.reduce((curObj, curPath) => curObj && curObj[curPath], obj);

// -------------------------------------------------
// getClientPreferencesByPath
//
// Given an preference list (same format as state), will return an object
// with the data for the given ID
//
// section is one of 'user', 'dataset', 'global'
//
// path is an array of keys to the preference you desire
//
// see types.js for path possibilities
//
/*  // TODO this will be relevant when /preferences/client is implemented on QCS
export function getClientPreferencesByPath(state, { group, path }) {

  if (!state.loadPending) {
    const prefs = getPreferences(state);
    if (group) {
      return path ? pathIn(prefs.client[group], path) : prefs.client[group];
    }
    // if no group,path is a  key
    if (path) {
      return pathIn(prefs.client, path);
    }
  }
  return null;
}
*/

// -------------------------------------------------
// getSharedPreferencesByPath
//
// Given an preference list (same format as state), will return an object
// with the data for the given ID
//
// section is one of 'user', 'dataset', 'global'
//
// path is an array of keys to the preference you desire
//
// see types.js for path possibilities
//
export function getSharedPreferencesByPath(state, { group, path }) {
  // if (!state.loadPending) {
  const prefs = getPreferences(state);
  if (group) {
    const retVal = path ? pathIn(prefs.shared[group], path) : prefs.shared[group];
    return retVal;
  }
  // if no group,path is a  key
  if (path) {
    return pathIn(prefs.shared, path);
  }
  // }
  return null;
}

export function allAccountPreferences(state) {
  const prefs = getSharedPreferencesByPath(state, { group: 'dataset', path: ['webApp'] });
  return prefs && prefs.accountsPrefs ? prefs.accountsPrefs : null;
}

export function accountPreferencesById(state) {
  const acctPrefs = allAccountPreferences(state);
  return acctPrefs && acctPrefs.byId;
}

export const getWebThemes = createSelector(
  (state) =>
    getSharedPreferencesByPath(state, { group: 'dataset', path: ['webApp', 'theme'] }) === 'ACME_DARK'
      ? 'dark'
      : 'light',
  // eslint-disable-next-line no-undef
  () => (window?.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'), // TODO: would not trigger update dynamically
  selectorsHelpers.createValueCache((current, system) => ({ current, system })),
);

export const getAccountNodePreferencesById = createSelector(
  (state) => getSharedPreferencesByPath(state, { group: 'dataset', path: ['webApp', 'accountNodePrefs'] }),
  (acctPrefs) => acctPrefs,
);

export const accountPreferencesForId = createSelector(
  (state, id) => id,
  (state) => getSharedPreferencesByPath(state, { group: 'dataset', path: ['webApp', 'accountsPrefs'] }),
  (accountId, accountsPrefs) => {
    if (accountsPrefs && accountsPrefs.byId) {
      const pref = accountsPrefs.byId[accountId];
      return pref;
    }
    return {};
  },
);

export const accountColorsById = createSelector(allAccountPreferences, (accountPrefs) => {
  if (accountPrefs && accountPrefs.byId) {
    return mapJSObject(accountPrefs.byId, (item) => colorLookup(item.color));
  }
  return {};
});

export const accountColorForId = createSelector(
  (state, id) => id,
  (state, id) => accountPreferencesForId(state, id),
  (accountId, pref) => {
    if (pref) {
      return colorLookup(pref.color);
    }
    return '#FFF';
  },
);

const dataPathObject = { group: 'dataset', path: ['webApp'] };
export const getDatasetPreferences = (state) => getSharedPreferencesByPath(state, dataPathObject);

// register preferences
const getRegisterPreferences = createSelector(
  getDatasetPreferences,
  (datasetPreferences) => datasetPreferences?.transactionRegister,
);
export const showFlags = createSelector(getRegisterPreferences, (registerPrefs) =>
  registerPrefs?.regFieldsSort?.cashFlow?.includes?.('userFlag'),
);
export const showReviewed = createSelector(getRegisterPreferences, (registerPrefs) =>
  registerPrefs?.regFieldsSort?.cashFlow?.includes?.('reviewed'),
);

// simple selectors to prevent rerenders on every pref change
export const getCategoryRulePreference = createSelector(
  getDatasetPreferences,
  (datasetPreferences) => datasetPreferences?.createCategoryRules,
);
export const getPayeeRulePreference = createSelector(
  getDatasetPreferences,
  (datasetPreferences) => datasetPreferences?.createPayeeRules,
);

export const getRegisterHeight = createSelector(getDatasetPreferences, (datasetPreferences) => {
  const pref = datasetPreferences?.transactionRegister?.registerComfort;
  if (pref === 'comfortable') {
    return 58;
  }
  if (pref === 'normal') {
    return 48;
  }
  return 40;
});

export const showGSF = createSelector(
  getDatasetPreferences,
  // setupStateIndex can be zero and it can be null too.
  (datasetPreferences) =>
    !datasetPreferences?.completedGSF &&
    (typeof datasetPreferences?.setupStateIndex === 'number' || !datasetPreferences?.setupStateIndex),
);

export const getWatchlistSetupCompleted = createSelector(
  getDatasetPreferences,
  (datasetPreferences) => !!datasetPreferences.watchlistSetupCompleted,
);

export const getHidePlanned = createSelector(getDatasetPreferences, (datasetPreferences) =>
  Boolean(datasetPreferences.hidePlanned),
);

export const getSpendingPlanSetupCompleted = createSelector(getDatasetPreferences, (datasetPreferences) =>
  datasetPreferences.spendingPlanSetupCompleted ? datasetPreferences.spendingPlanSetupCompleted : false,
);

export const getShowBalance = createSelector(
  getDatasetPreferences,
  (datasetPreferences) => datasetPreferences.showBalance,
);

const cardRecord = Record({ key: undefined, value: false });
export const getUserCards = createSelector(getDatasetPreferences, (datasetPreferences) =>
  datasetPreferences.overviewPageLayout.map((card) =>
    cardRecord({
      key: card.get('key') || 'Unknown',
      value: Boolean(card.get('value')),
    }),
  ),
);
